import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, ExternalLink } from 'lucide-react';

// 3.30 — AIEnginesSection: rich breakdown of the 8 AI engines
// Replaces the bare 8-card grid in AEOSection with a "what each does for you" angle
const AIEnginesSection = () => {
  const { t } = useTranslation();

  const engines = [
    { id: 'chatgpt',    name: 'ChatGPT',       vendor: 'OpenAI',        users: '700M+',   color: '#10A37F', accent: '#10A37F' },
    { id: 'claude',     name: 'Claude',        vendor: 'Anthropic',     users: '200M+',   color: '#D97706', accent: '#D97706' },
    { id: 'gemini',     name: 'Gemini / SGE',  vendor: 'Google AI',     users: '70%*',    color: '#4285F4', accent: '#4285F4' },
    { id: 'perplexity', name: 'Perplexity',    vendor: 'Answer engine', users: '25M MAU', color: '#20808D', accent: '#20808D' },
    { id: 'grok',       name: 'Grok',          vendor: 'xAI / X',       users: 'fast-growing', color: '#1DA1F2', accent: '#1DA1F2' },
    { id: 'copilot',    name: 'Bing Copilot',  vendor: 'Microsoft Edge',users: '4-5%',    color: '#0078D4', accent: '#0078D4' },
    { id: 'meta',       name: 'Meta AI',       vendor: 'WhatsApp / IG', users: '3B+',     color: '#0866FF', accent: '#0866FF' },
    { id: 'ddg',        name: 'DuckDuckGo AI', vendor: 'Privacy-first', users: '~5%',     color: '#DE5833', accent: '#DE5833' },
  ];

  return (
    <section className="py-24 px-6 relative overflow-hidden" data-testid="ai-engines-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0d0a14] to-[#050505]" />
      <div className="absolute top-0 left-1/3 w-96 h-96 bg-[#8B5CF6]/8 rounded-full blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-14 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 mb-6">
            <Sparkles className="w-4 h-4 text-[#8B5CF6]" />
            <span className="text-sm text-[#c4b5fd]">{t('aiEngines.badge')}</span>
          </div>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-medium tracking-tight mb-6">
            {t('aiEngines.title1')}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#8B5CF6] to-[#EC4899]">
              {t('aiEngines.title2')}
            </span>
          </h2>
          <p className="text-lg text-[#A1A1AA] max-w-3xl mx-auto leading-relaxed">
            {t('aiEngines.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {engines.map((eng, idx) => (
            <div
              key={eng.id}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-white/20 transition-all animate-fade-up"
              style={{ animationDelay: `${idx * 0.05}s` }}
              data-testid={`ai-engine-${eng.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                    style={{ background: `linear-gradient(135deg, ${eng.color}, ${eng.color}80)` }}
                  >
                    {eng.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-base font-semibold text-white">{eng.name}</div>
                    <div className="text-xs text-[#A1A1AA]">{eng.vendor}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-[#71717A]">{t('aiEngines.usersLabel')}</div>
                  <div className="text-sm font-semibold" style={{ color: eng.accent }}>{eng.users}</div>
                </div>
              </div>

              <p className="text-sm text-[#d4d4d8] leading-relaxed mb-4">
                {t(`aiEngines.engines.${eng.id}.what`)}
              </p>

              <div className="rounded-lg bg-black/40 border border-white/5 p-3">
                <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">
                  {t('aiEngines.orchestraDoesLabel')}
                </div>
                <p className="text-xs text-[#a1a1aa] leading-relaxed">
                  {t(`aiEngines.engines.${eng.id}.orchestra`)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-center text-[#71717A] mt-6 italic">
          {t('aiEngines.footnote')}
        </p>
      </div>
    </section>
  );
};

export default AIEnginesSection;
